import { useEffect, useId, useRef, useState } from "react";
import { RequiredMark, cx } from "./ui";
import { searchAddresses } from "@/lib/api";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { Address, GeocodeResult } from "@/lib/types";

/**
 * The street address field. Type into it, pick a suggestion, and the location
 * fields around it fill themselves.
 *
 * This is the street field, not a box that sits above one. It used to be a
 * separate "Search for an address" input, which meant typing the same street
 * twice -- once to find the place, once into the field below it -- and left
 * two versions of the truth on screen that could disagree.
 *
 * Why it searches at all rather than letting people type freely: a typed
 * address has no coordinate, so pricing has to geocode the text server-side and
 * hope, and a misspelled suburb quietly becomes a driver at the wrong gate.
 * Picking a suggestion carries a coordinate through to the quote, which is what
 * lets the rate engine measure a real route.
 *
 * Typing is never blocked and a suggestion is never forced. A geocoder knows
 * where a street is; it does not know the unit number, the gate code, or that
 * the entrance is round the back -- so the surrounding fields stay editable and
 * the server still geocodes typed text when nobody picked anything.
 *
 * It is a combobox, with the keyboard behaviour that implies -- arrows to move,
 * Enter to choose, Escape to dismiss -- because an address box that only works
 * with a mouse is an address box a screen reader user cannot complete, and this
 * one sits in the middle of the booking form.
 */
export function AddressSearch({
  value,
  onChange,
  authenticated = true,
  label = "Street address",
  name,
  error,
  required,
  className,
}: {
  value: Address;
  onChange: (next: Address) => void;
  /** False on pages reached without a session: guest checkout, signup. */
  authenticated?: boolean;
  label?: string;
  /** Field id, so a validation summary can focus it like any other field. */
  name?: string;
  error?: string;
  required?: boolean;
  className?: string;
}) {
  // null means "nobody is typing": the field is showing an address that was
  // picked, restored from a saved address, or prefilled for the signed-in
  // account. Only a keystroke starts a lookup, so arriving on a prefilled form
  // does not fire one.
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [open, setOpen] = useState(false);

  const debounced = useDebouncedValue(query ?? "", 400);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (debounced.trim().length < 3) {
      setResults(null);
      setFailed(false);
      return;
    }
    // Cancelled rather than aborted: a stale response that arrives after a
    // newer one must not overwrite the newer list.
    let cancelled = false;
    setSearching(true);
    searchAddresses(debounced, authenticated)
      .then((response) => {
        if (cancelled) return;
        setResults(response.data ?? []);
        setFailed(false);
        setHighlighted(0);
        setOpen(true);
      })
      .catch(() => {
        if (cancelled) return;
        // A lookup outage must not block a booking: what was typed stands, the
        // fields around it are still there, and the server geocodes typed text.
        setResults([]);
        setFailed(true);
        setOpen(true);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, authenticated]);

  // Clicking away closes the list. Without this it hangs over whatever the
  // person moved on to fill in.
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  function type(text: string) {
    setQuery(text);
    // A hand-typed street describes a different place to the one the pin came
    // from, so the coordinate goes with it.
    onChange({ ...value, street: text, latitude: undefined, longitude: undefined });
  }

  function choose(result: GeocodeResult) {
    onChange({
      ...value,
      street: result.street || value.street,
      suburb: result.suburb || value.suburb,
      city: result.city || value.city,
      province: result.province || value.province,
      postal_code: result.postal_code || value.postal_code,
      country: result.country || value.country,
      latitude: result.latitude,
      longitude: result.longitude,
    });
    setQuery(null);
    setResults(null);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || !results?.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      // Only when a suggestion is actually highlighted, so Enter in an empty
      // box still submits the form around it.
      event.preventDefault();
      choose(results[highlighted]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  const inputId = name ?? `${listId}-input`;
  const showList = open && results !== null;

  return (
    <div ref={containerRef} className={cx("relative", className)}>
      <label className="ws-label" htmlFor={inputId}>
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        id={inputId}
        name={name}
        className={cx("ws-input", error && "border-signal-bad focus:border-signal-bad focus:ring-signal-bad/20")}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-required={required ? true : undefined}
        aria-invalid={error ? true : undefined}
        aria-activedescendant={showList && results?.length ? `${listId}-${highlighted}` : undefined}
        autoComplete="off"
        placeholder="Start typing a street, suburb or complex"
        value={value.street}
        onChange={(event) => type(event.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {error ? (
        <p className="ws-error">{error}</p>
      ) : (
        <p className="ws-hint">
          {searching ? "Searching…" : "Pick a suggestion so we can price the exact route."}
        </p>
      )}

      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-ink-200 bg-white shadow-lg"
        >
          {results.length === 0 ? (
            <li className="px-3 py-3 text-sm text-ink-500">
              {failed
                ? "Address lookup is unavailable right now — fill the fields in below instead."
                : "No matches. Keep what you typed, or try a nearby street or landmark."}
            </li>
          ) : (
            results.map((result, index) => (
              <li
                key={`${result.label}-${index}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === highlighted}
                className={cx(
                  "cursor-pointer px-3 py-2 text-sm",
                  index === highlighted ? "bg-brand/10 text-ink-900" : "text-ink-700",
                )}
                onMouseEnter={() => setHighlighted(index)}
                // mousedown, not click: the input's blur would close the list
                // before a click ever lands.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(result);
                }}
              >
                {result.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
